import { useState, useEffect, useRef } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { doc, getDoc } from "firebase/firestore"
import { db } from "../services/firebase"
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
} from "recharts"
import VideoPlayer from "../components/VideoPlayer"
import ReportGenerating from "../components/ReportGenerating"

// ── Helpers ───────────────────────────────────────────────────────────────────
const scoreColor = (s) => {
  const n = parseFloat(s)
  if (n >= 7) return "#16a34a"
  if (n >= 5) return "#d97706"
  return "#dc2626"
}
const scoreLabel = (s) => {
  const n = parseFloat(s)
  if (n >= 7) return "Good"
  if (n >= 5) return "Average"
  return "Poor"
}
const scoreBadgeStyle = (s) => {
  const n = parseFloat(s)
  if (n >= 7)
    return {
      background: "#f0fdf4",
      border: "1px solid #bbf7d0",
      color: "#15803d",
    }
  if (n >= 5)
    return {
      background: "#fffbeb",
      border: "1px solid #fde68a",
      color: "#b45309",
    }
  return {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#dc2626",
  }
}

// ── Primitives ────────────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 mb-3">
    {children}
  </p>
)

const Badge = ({ children, style }) => (
  <span
    className="inline-block text-[11px] font-bold uppercase tracking-widest px-2 py-0.5"
    style={{ borderRadius: 0, ...style }}
  >
    {children}
  </span>
)

const ScoreBar = ({ score, max = 10 }) => {
  const pct = (parseFloat(score) / max) * 100
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 bg-zinc-100">
        <div
          style={{
            width: `${pct}%`,
            background: scoreColor(score),
            height: "100%",
          }}
        />
      </div>
      <span
        className="text-sm font-bold shrink-0"
        style={{
          fontFamily: "'IBM Plex Mono', monospace",
          color: scoreColor(score),
          minWidth: 36,
        }}
      >
        {score}/10
      </span>
    </div>
  )
}

const DotList = ({ items, color }) => (
  <div className="space-y-2">
    {items.map((p, i) => (
      <div key={i} className="flex items-start gap-2.5">
        <div
          className="w-1 h-1 shrink-0 mt-1.5"
          style={{ background: color }}
        />
        <p className="text-sm text-zinc-700 leading-relaxed">{p}</p>
      </div>
    ))}
  </div>
)

// ── Transcript bubble ─────────────────────────────────────────────────────────
const Bubble = ({ role, text }) => {
  const isAI = role === "assistant"
  return (
    <div className={`flex ${isAI ? "justify-start" : "justify-end"} mb-3`}>
      {isAI && (
        <div
          className="w-5 h-5 border border-zinc-200 flex items-center justify-center shrink-0 mr-2.5 mt-0.5 bg-zinc-50"
          style={{ borderRadius: 0 }}
        >
          <span
            className="text-[9px] font-bold text-zinc-400"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            AI
          </span>
        </div>
      )}
      <div
        className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed border`}
        style={{
          borderRadius: 0,
          ...(isAI
            ? {
                background: "#ffffff",
                borderColor: "#e4e4e7",
                color: "#3f3f46",
              }
            : {
                background: "#eef2ff",
                borderColor: "#c7d2fe",
                color: "#3730a3",
              }),
        }}
      >
        {text}
      </div>
      {!isAI && (
        <div
          className="w-5 h-5 border flex items-center justify-center shrink-0 ml-2.5 mt-0.5"
          style={{
            borderRadius: 0,
            background: "#eef2ff",
            borderColor: "#c7d2fe",
          }}
        >
          <span
            className="text-[9px] font-bold"
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              color: "#4f46e5",
            }}
          >
            C
          </span>
        </div>
      )}
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
const ReportPage = () => {
  const { reportId } = useParams()
  const navigate = useNavigate()

  const [phase, setPhase] = useState("polling") // polling | fetching | done | error
  const [reportData, setReportData] = useState(null)
  const [activeTab, setActiveTab] = useState("feedback")
  const [mainTab, setMainTab] = useState("report")
  const [anomalyExpanded, setAnomalyExpanded] = useState(false)
  const [cheatingVerdict, setCheatingVerdict] = useState(null)
  const pollRef = useRef(null)

  const videoRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isVideoReady, setIsVideoReady] = useState(false)

  // ── Step 1: Poll Firestore until feedbackStatus === "generated" ──────────────
  useEffect(() => {
    if (!reportId) return

    const pollFirestore = async () => {
      try {
        const snap = await getDoc(doc(db, "rooms", reportId))
        if (snap.exists()) {
          const roomData = snap.data()
          const feedbackStatus = roomData.feedbackStatus
          if (feedbackStatus === "generated") {
            clearInterval(pollRef.current)
            setPhase("fetching")
          }
        }
      } catch (err) {
        console.error("Firestore poll error:", err)
      }
    }

    pollFirestore()
    pollRef.current = setInterval(pollFirestore, 3000)
    return () => clearInterval(pollRef.current)
  }, [reportId])

  // ── Step 2: Once feedbackStatus is generated, fetch the report API ───────────
  useEffect(() => {
    if (phase !== "fetching") return

    const fetchReport = async () => {
      try {
        const res = await fetch(
          `https://hire100x-e1dd4.uc.r.appspot.com/reports?meetingId=${reportId}`,
        )
        const json = await res.json()
        if (json.success && json.data) {
          setReportData({ ...json.data, transcripts: json.transcripts })
          setPhase("done")
        } else {
          setPhase("error")
        }
      } catch (err) {
        console.error("Report fetch error:", err)
        setPhase("error")
      }
    }

    fetchReport()
  }, [phase, reportId])

  // ── Loading state ─────────────────────────────────────────────────────────────
  if (phase !== "done" || !reportData) {
    return <ReportGenerating phase={phase} />
  }

  // ── Destructure from real API data ────────────────────────────────────────────
  const {
    candidate_rating,
    communication_skills,
    skill_by_skill_rating,
    explanations,
    candidate,
    position,
    date,
    duration,
    total_non_logistic_questions,
    total_non_logistic_questions_answered,
    cheating_probability,
    rating_justification,
    final_verdict,
    recordingUrl,
    transcripts,
  } = reportData

  const commScore = parseFloat(communication_skills.score)
  const overallSkillsAvg =
    skill_by_skill_rating.reduce((acc, s) => acc + parseFloat(s.rating), 0) /
    skill_by_skill_rating.length
  const radarData = skill_by_skill_rating.map((s) => ({
    subject: s.name_of_the_skill_tested,
    value: parseFloat(s.rating),
    fullMark: 10,
  }))
  const verdictPassed = final_verdict === "True" || final_verdict === "Yes"

  return (
    <div
      className="h-screen flex flex-col overflow-hidden bg-zinc-50"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        .hide-scroll::-webkit-scrollbar { display: none; }
        .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* ── Nav ── */}
      <div className="bg-white border-b border-zinc-200 shrink-0 z-20">
        <div className="px-10 py-3 flex items-center justify-between">
          <span
            onClick={() => navigate("/")}
            className="text-lg font-bold text-zinc-900 tracking-tight"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            Resolut
          </span>
          <button
            className="bg-zinc-900 text-white text-[10px] font-bold px-4 py-2 uppercase tracking-widest hover:bg-zinc-700 transition-colors"
            style={{ borderRadius: 0 }}
          >
            Talk to Sales
          </button>
        </div>
        {/* Page-level tabs */}
        <div className="flex px-10 border-t border-zinc-100">
          {[
            {
              key: "report",
              label: "Interview Report",
              icon: (
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 2H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6L9 2z" />
                  <path d="M9 2v4h4M6 9h4M6 11h4M6 7h1" />
                </svg>
              ),
            },
            {
              key: "recording",
              label: "Interview Recording",
              icon: (
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="1" y="3" width="10" height="10" rx="1" />
                  <path d="M11 6l4-2v8l-4-2V6z" />
                </svg>
              ),
            },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setMainTab(key)}
              className={`flex items-center gap-2 px-4 py-3 text-[11px] font-bold uppercase tracking-widest transition-all relative mr-1 ${
                mainTab === key
                  ? "text-zinc-900"
                  : "text-zinc-400 hover:text-zinc-600"
              }`}
              style={{ borderRadius: 0 }}
            >
              {icon}
              {label}
              {mainTab === key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Two-panel body ── */}
      {mainTab === "report" ? (
        <div className="flex flex-1 min-h-0 bg-zinc-100 gap-px">
          {/* ════ LEFT — scrollable analysis ════ */}
          <div className="flex-1 flex flex-col min-w-0 overflow-y-auto hide-scroll bg-zinc-50">
            {/* Candidate header */}
            <div className="bg-zinc-50 px-10 py-5 shrink-0">
              <div className="bg-white border border-zinc-200 px-8 py-5 flex items-center justify-between gap-6">
                <div className="flex flex-col gap-2 min-w-0">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h1 className="text-xl font-bold text-zinc-900">
                      {candidate?.name ?? "Candidate"}
                    </h1>
                    <Badge
                      style={{
                        background: "#ede9fe",
                        color: "#6d28d9",
                        border: "1px solid #ddd6fe",
                      }}
                    >
                      {position ?? "Engineer"}
                    </Badge>
                    <Badge
                      style={
                        verdictPassed
                          ? {
                              background: "#f0fdf4",
                              color: "#15803d",
                              border: "1px solid #bbf7d0",
                            }
                          : {
                              background: "#fef2f2",
                              color: "#dc2626",
                              border: "1px solid #fecaca",
                            }
                      }
                    >
                      {verdictPassed ? "Passed" : "Not Selected"}
                    </Badge>
                  </div>
                  <div
                    className="flex items-center gap-4 text-[13px] text-zinc-400 flex-wrap"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    <span>
                      Interviewed:{" "}
                      <span className="text-zinc-600">
                        {date
                          ? new Date(date).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : new Date().toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                      </span>
                    </span>
                    <span className="text-zinc-200">|</span>
                    <span>
                      Duration:{" "}
                      <span className="text-zinc-600">{duration ?? "—"}</span>
                    </span>
                    <span className="text-zinc-200">|</span>
                    <span>
                      Questions:{" "}
                      <span className="text-zinc-600">
                        {total_non_logistic_questions_answered}/
                        {total_non_logistic_questions}
                      </span>
                    </span>
                  </div>
                </div>

                {/* Candidate score */}
                <div className="shrink-0 text-right px-6 py-2">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 mb-1">
                    Score
                  </p>
                  <p
                    className="text-4xl font-bold leading-none"
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      color: scoreColor(candidate_rating),
                    }}
                  >
                    {candidate_rating}
                  </p>
                  <p className="text-[11px] text-zinc-400 mt-1">/10</p>
                </div>
              </div>
            </div>

            {/* Stat strip */}
            <div className="shrink-0 bg-zinc-50 px-10 pb-5">
              <div className="grid grid-cols-4 bg-white border border-zinc-200">
                {[
                  {
                    label: "Communication",
                    value: `${commScore}/10`,
                    sub: scoreLabel(commScore),
                  },
                  {
                    label: "Skills Avg",
                    value: `${overallSkillsAvg.toFixed(1)}/10`,
                    sub: scoreLabel(overallSkillsAvg),
                  },
                  { label: "CEFR Level", value: "B1", sub: "Intermediate" },
                  {
                    label: "Cheating Risk",
                    value: `${cheating_probability}%`,
                    sub: "Low risk",
                  },
                ].map((s, i, arr) => (
                  <div
                    key={s.label}
                    className={`px-6 py-4 ${i < arr.length - 1 ? "border-r border-zinc-200" : ""}`}
                  >
                    <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 mb-1">
                      {s.label}
                    </p>
                    <p
                      className="text-xl font-bold text-zinc-900"
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      {s.value}
                    </p>
                    <p className="text-[13px] text-zinc-400 mt-0.5">{s.sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Content sections */}
            <div className="px-10 py-7 space-y-4">
              {/* Interview Analysis */}
              <div className="bg-white border border-zinc-200">
                <div className="px-6 py-3.5 border-b border-zinc-200">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">
                    Interview Analysis
                  </p>
                </div>
                <div className="px-6 py-5 grid grid-cols-3 gap-6 divide-x divide-zinc-100">
                  <div>
                    <SectionLabel>Strengths</SectionLabel>
                    <DotList
                      items={explanations.candidate_positives}
                      color="#34d399"
                    />
                  </div>
                  <div className="pl-6">
                    <SectionLabel>Weaknesses</SectionLabel>
                    <DotList
                      items={explanations.candidate_negatives}
                      color="#f87171"
                    />
                  </div>
                  <div className="pl-6">
                    <SectionLabel>Areas of Improvement</SectionLabel>
                    <DotList
                      items={explanations.areas_of_improvement}
                      color="#fbbf24"
                    />
                  </div>
                </div>
                <div className="px-6 pb-5 pt-4 border-t border-zinc-100">
                  <SectionLabel>Overall Assessment</SectionLabel>
                  <p className="text-sm text-zinc-500 leading-relaxed">
                    {rating_justification}
                  </p>
                </div>
              </div>

              {/* Skills + Communication — stacked */}
              <div className="flex flex-col gap-4">
                {/* Skills */}
                <div className="bg-white border border-zinc-200">
                  <div className="px-6 py-3.5 border-b border-zinc-200">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">
                      Skills Analysis
                    </p>
                  </div>
                  <div className="px-6 py-5 flex gap-6">
                    {/* Radar */}
                    <div style={{ width: 220, minWidth: 220 }}>
                      <div style={{ height: 200 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={radarData}>
                            <PolarGrid stroke="#e5e7eb" />
                            <PolarAngleAxis
                              dataKey="subject"
                              tick={{
                                fontSize: 10,
                                fill: "#9ca3af",
                                fontFamily: "'IBM Plex Sans', sans-serif",
                              }}
                            />
                            <Radar
                              name="Score"
                              dataKey="value"
                              stroke="#18181b"
                              fill="#18181b"
                              fillOpacity={0.07}
                              strokeWidth={1.5}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-3 pt-3 border-t border-zinc-100">
                        <div className="flex items-center justify-between mb-2">
                          <SectionLabel>Overall Match</SectionLabel>
                          <span
                            className="text-sm font-bold"
                            style={{
                              fontFamily: "'IBM Plex Mono', monospace",
                              color: scoreColor(overallSkillsAvg),
                            }}
                          >
                            {overallSkillsAvg.toFixed(1)}/10
                          </span>
                        </div>
                        <div className="h-2 bg-zinc-100">
                          <div
                            style={{
                              width: `${(overallSkillsAvg / 10) * 100}%`,
                              background: scoreColor(overallSkillsAvg),
                              height: "100%",
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="w-px bg-zinc-100 shrink-0" />

                    {/* Breakdown */}
                    <div className="flex-1 min-w-0">
                      <SectionLabel>Skill Breakdown</SectionLabel>
                      <div className="space-y-4">
                        {skill_by_skill_rating.map((s) => (
                          <div key={s.name_of_the_skill_tested}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-semibold text-zinc-700">
                                {s.name_of_the_skill_tested}
                              </span>
                              <span
                                className="text-[11px] font-bold px-1.5 py-0.5"
                                style={{
                                  ...scoreBadgeStyle(s.rating),
                                  borderRadius: 0,
                                  fontFamily: "'IBM Plex Mono', monospace",
                                }}
                              >
                                {scoreLabel(s.rating)}
                              </span>
                            </div>
                            <ScoreBar score={s.rating} />
                            <p className="text-[13px] text-zinc-400 mt-1 leading-snug">
                              {s.reasoning}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Communication */}
                <div className="bg-white border border-zinc-200">
                  <div className="px-6 py-3.5 border-b border-zinc-200 flex items-center justify-between">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">
                      Communication Skills
                    </p>
                    <span
                      className="text-[11px] font-bold px-1.5 py-0.5"
                      style={{
                        ...scoreBadgeStyle(commScore),
                        borderRadius: 0,
                        fontFamily: "'IBM Plex Mono', monospace",
                      }}
                    >
                      CEFR B1
                    </span>
                  </div>
                  <div className="px-6 py-5 flex gap-6">
                    {/* Left — big score + radial breakdown */}
                    <div
                      className="shrink-0 flex flex-col items-center justify-center gap-3"
                      style={{ width: 130 }}
                    >
                      <div className="text-center">
                        <p
                          className="text-5xl font-bold leading-none"
                          style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            color: scoreColor(commScore),
                          }}
                        >
                          {commScore}
                        </p>
                        <p className="text-[11px] text-zinc-400 mt-1">
                          /10 overall
                        </p>
                      </div>
                      <div
                        className="text-xs font-bold px-3 py-1.5 w-full text-center"
                        style={{
                          ...scoreBadgeStyle(commScore),
                          borderRadius: 0,
                        }}
                      >
                        {scoreLabel(commScore)}
                      </div>
                      <p className="text-[11px] text-zinc-400 text-center leading-snug mt-1">
                        {communication_skills.feedback}
                      </p>
                    </div>

                    {/* Divider */}
                    <div className="w-px bg-zinc-100 shrink-0" />

                    {/* Right — category tiles */}
                    <div className="flex-1 min-w-0">
                      <SectionLabel>Dimension Scores</SectionLabel>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(
                          communication_skills.score_breakdown,
                        ).map(([k, v]) => {
                          const bs = scoreBadgeStyle(v)
                          return (
                            <div
                              key={k}
                              className="flex items-center justify-between px-3 py-2.5 border border-zinc-100 bg-zinc-50"
                              style={{ borderRadius: 0 }}
                            >
                              <span className="text-[12px] text-zinc-600 leading-tight pr-2">
                                {k}
                              </span>
                              <span
                                className="text-sm font-bold shrink-0"
                                style={{
                                  fontFamily: "'IBM Plex Mono', monospace",
                                  color: bs.color,
                                }}
                              >
                                {v}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ════ RIGHT — transcript / Q-by-Q (fixed height, scrolls internally) ════ */}
          <div
            className="flex flex-col bg-white shrink-0"
            style={{ width: "36%" }}
          >
            {/* Tabs */}
            <div className="flex border-b border-zinc-200 shrink-0 bg-white">
              <button
                onClick={() => setActiveTab("transcript")}
                className={`flex-1 px-4 py-4 text-[11px] font-bold uppercase tracking-widest border-r border-zinc-200 transition-all relative ${
                  activeTab === "transcript"
                    ? "text-zinc-900 bg-white"
                    : "text-zinc-400 bg-white hover:text-zinc-600 hover:bg-zinc-50"
                }`}
                style={{ borderRadius: 0 }}
              >
                Transcript
                {activeTab === "transcript" && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-zinc-900" />
                )}
              </button>
              <button
                onClick={() => setActiveTab("feedback")}
                className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-4 text-[11px] font-bold uppercase tracking-widest transition-all relative ${
                  activeTab === "feedback"
                    ? "text-zinc-900 bg-white"
                    : "text-zinc-400 bg-white hover:text-zinc-600 hover:bg-zinc-50"
                }`}
                style={{ borderRadius: 0 }}
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm.75 10.5h-1.5v-5h1.5v5zm0-6.5h-1.5V3.5h1.5V5z" />
                </svg>
                Q by Q Feedback
                {activeTab === "feedback" && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-zinc-900" />
                )}
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto hide-scroll">
              {activeTab === "transcript" ? (
                <div className="px-7 py-6">
                  {(reportData.transcripts ?? []).map((msg, i) => (
                    <Bubble
                      key={i}
                      role={msg.sender === "interviewer" ? "assistant" : "user"}
                      text={msg.text}
                    />
                  ))}
                </div>
              ) : (
                <div className="px-6 py-6 space-y-3">
                  {explanations.question_by_question_feedback.map((q, i) => (
                    <div key={i} className="border border-zinc-200">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-3 px-4 py-3.5 bg-zinc-50 border-b border-zinc-200">
                        <p className="text-sm font-semibold text-zinc-800 leading-snug flex-1">
                          <span
                            className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 mr-2"
                            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                          >
                            Q{i + 1}
                          </span>
                          {q.question}
                        </p>
                        <div className="shrink-0 flex flex-col items-end gap-1">
                          <span
                            className="text-base font-bold"
                            style={{
                              fontFamily: "'IBM Plex Mono', monospace",
                              color: scoreColor(q.score),
                            }}
                          >
                            {q.score}/10
                          </span>
                          <Badge style={scoreBadgeStyle(q.score)}>
                            {scoreLabel(q.score)}
                          </Badge>
                        </div>
                      </div>
                      {/* Answer */}
                      <div className="px-4 py-3.5 border-b border-zinc-100">
                        <SectionLabel>Candidate's Answer</SectionLabel>
                        <p
                          className="text-[12px] text-zinc-600 leading-relaxed bg-zinc-50 px-3 py-2.5 border border-zinc-100"
                          style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            borderRadius: 0,
                          }}
                        >
                          {q.answer}
                        </p>
                      </div>
                      {/* Feedback */}
                      <div className="px-4 py-3.5">
                        <SectionLabel>Feedback</SectionLabel>
                        <p className="text-sm text-zinc-600 leading-relaxed">
                          {q["analysis/feedback"]}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ════ RECORDING TAB ════ */
        <div className="flex flex-1 min-h-0 bg-zinc-100 gap-px">
          {/* ── LEFT: Integrity Analysis (50%) ── */}
          <div
            className="flex flex-col bg-zinc-50 overflow-y-auto hide-scroll"
            style={{ width: "50%" }}
          >
            {/* Header */}
            <div className="bg-zinc-50 px-10 py-3 shrink-0">
              <div className="bg-white border border-zinc-200 px-6 py-3">
                <div className="flex items-center justify-between gap-4">
                  {/* Left: title */}
                  <div className="flex items-center gap-2">
                    <div
                      className="w-5 h-5 flex items-center justify-center shrink-0"
                      style={{
                        background: "#fffbeb",
                        border: "1px solid #fde68a",
                      }}
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 16 16"
                        fill="#b45309"
                      >
                        <path d="M8 1L1 14h14L8 1zm0 3l5.5 9.5h-11L8 4zm-.75 3.5v3h1.5v-3h-1.5zm0 4v1.5h1.5V11.5h-1.5z" />
                      </svg>
                    </div>
                    <h2 className="text-sm font-bold text-zinc-900">
                      Integrity Analysis
                    </h2>
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5"
                      style={{
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        color: "#dc2626",
                      }}
                    >
                      NEEDS REVIEW
                    </span>
                  </div>

                  {/* Right: score tiles + risk score inline */}
                  <div className="flex items-center gap-3">
                    {[
                      {
                        label: "Gaze",
                        score:
                          reportData.gazeAnalysis?.cheating_probability ?? 55,
                        color: "#b45309",
                        bg: "#fffbeb",
                        border: "#fde68a",
                      },
                      {
                        label: "Transcript",
                        score:
                          reportData.transcriptAnalysis?.cheating_probability ??
                          85,
                        color: "#dc2626",
                        bg: "#fef2f2",
                        border: "#fecaca",
                      },
                    ].map(({ label, score, color, bg, border }) => (
                      <div
                        key={label}
                        className="flex items-center gap-2 px-3 py-1.5"
                        style={{
                          background: bg,
                          border: `1px solid ${border}`,
                        }}
                      >
                        <span
                          className="text-[10px] font-bold uppercase tracking-widest"
                          style={{ color }}
                        >
                          {label}
                        </span>
                        <span
                          className="text-base font-bold"
                          style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            color,
                          }}
                        >
                          {score}%
                        </span>
                      </div>
                    ))}

                    <div className="pl-3 border-l border-zinc-200 text-right">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 leading-none mb-0.5">
                        Risk
                      </p>
                      <p
                        className="text-2xl font-bold leading-none"
                        style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          color: "#dc2626",
                        }}
                      >
                        7.2
                      </p>
                      <p className="text-[10px] text-zinc-400">/10</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-10 space-y-4 pb-8">
              {/* Transcript Analysis Summary */}
              <div className="bg-white border border-zinc-200">
                <div className="px-6 py-3.5 border-b border-zinc-200 flex items-center gap-2">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="#3730a3"
                  >
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
                  </svg>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">
                    Transcript Analysis
                  </p>
                  <span
                    className="ml-auto text-[10px] font-bold px-2 py-0.5"
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      color: "#dc2626",
                      borderRadius: 0,
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}
                  >
                    85% risk
                  </span>
                </div>
                <div className="px-6 py-4">
                  <p className="text-sm text-zinc-600 leading-relaxed">
                    {reportData.transcriptAnalysis?.anomaly_summary ??
                      "The candidate's answers were unusually polished and structured perfectly, covering context, action, and outcome without any hesitation or need for clarification. They provided a textbook response with no personal anecdotes or specific examples. The transitions between points were unnaturally smooth, and there were no genuine moments of confusion or backtracking."}
                  </p>
                </div>
              </div>

              {/* Gaze Analysis Summary */}
              <div className="bg-white border border-zinc-200">
                <div className="px-6 py-3.5 border-b border-zinc-200 flex items-center gap-2">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="#b45309"
                  >
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5C21.27 7.61 17 4.5 12 4.5zm0 12.5a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
                  </svg>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">
                    Gaze Analysis
                  </p>
                  <span
                    className="ml-auto text-[10px] font-bold px-2 py-0.5"
                    style={{
                      background: "#fffbeb",
                      border: "1px solid #fde68a",
                      color: "#b45309",
                      borderRadius: 0,
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}
                  >
                    55% risk
                  </span>
                </div>
                <div className="px-6 py-4">
                  <p className="text-sm text-zinc-600 leading-relaxed">
                    {reportData.gazeAnalysis?.anomaly_summary ??
                      "The candidate's gaze shifted consistently to the right across 3 of 4 valid answers, indicating a notable pattern. Although the stability ratio is low, the consistency of the directional shift is concerning and suggests potential off-screen reference use."}
                  </p>
                </div>
              </div>

              {/* Merged Flagged Timestamps — 2-column */}
              <div className="bg-white border border-zinc-200">
                <div className="px-6 py-3.5 border-b border-zinc-200 flex items-center justify-between">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">
                    Flagged Timestamps
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <div
                        className="w-2 h-2"
                        style={{ background: "#dc2626" }}
                      />
                      <span className="text-[10px] text-zinc-400">
                        Transcript
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div
                        className="w-2 h-2"
                        style={{ background: "#b45309" }}
                      />
                      <span className="text-[10px] text-zinc-400">Gaze</span>
                    </div>
                  </div>
                </div>
                <div className="px-6 py-4 grid grid-cols-2 gap-3">
                  {[
                    ...(
                      reportData.transcriptAnalysis?.flagged_timestamps ?? []
                    ).map((f) => ({
                      ...f,
                      source: "transcript",
                    })),
                    ...(reportData.gazeAnalysis?.flagged_timestamps ?? []).map(
                      (f) => ({
                        ...f,
                        source: "gaze",
                      }),
                    ),
                  ].map(
                    (
                      { timestamp, title, description, severity, source },
                      i,
                    ) => {
                      const isGaze = source === "gaze"
                      const severityStyle =
                        severity === "high"
                          ? {
                              background: "#fef2f2",
                              border: "1px solid #fecaca",
                              color: "#dc2626",
                            }
                          : severity === "medium"
                            ? {
                                background: "#fffbeb",
                                border: "1px solid #fde68a",
                                color: "#b45309",
                              }
                            : {
                                background: "#eff6ff",
                                border: "1px solid #bfdbfe",
                                color: "#1d4ed8",
                              }

                      return (
                        <div
                          key={i}
                          className="flex flex-col gap-2 p-3"
                          style={{
                            border: "1px solid #f4f4f5",
                            background: "#fafafa",
                            borderRadius: 0,
                          }}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span
                              className="text-[11px] font-bold px-2 py-0.5 shrink-0"
                              style={{
                                ...severityStyle,
                                borderRadius: 0,
                                fontFamily: "'IBM Plex Mono', monospace",
                              }}
                            >
                              {timestamp}
                            </span>
                            <span
                              className="text-[9px] font-bold px-1.5 py-0.5 uppercase tracking-widest shrink-0"
                              style={{
                                borderRadius: 0,
                                background: isGaze ? "#fffbeb" : "#eff6ff",
                                border: isGaze
                                  ? "1px solid #fde68a"
                                  : "1px solid #bfdbfe",
                                color: isGaze ? "#b45309" : "#3730a3",
                              }}
                            >
                              {isGaze ? "Gaze" : "Speech"}
                            </span>
                          </div>
                          <p className="text-[12px] font-semibold text-zinc-800 leading-snug">
                            {title}
                          </p>
                          <p className="text-[11px] text-zinc-400 leading-snug">
                            {description}
                          </p>
                        </div>
                      )
                    },
                  )}
                </div>
              </div>

              {/* Verdict */}
              <div className="bg-white border border-zinc-200">
                <div className="px-6 py-3.5 border-b border-zinc-200">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">
                    Your Verdict
                  </p>
                </div>
                <div className="px-6 py-4 flex gap-3">
                  <button
                    onClick={() => setCheatingVerdict("cheating")}
                    className="flex-1 flex items-center justify-center gap-2 py-3 text-[11px] font-bold uppercase tracking-widest border transition-colors"
                    style={{
                      borderRadius: 0,
                      background:
                        cheatingVerdict === "cheating" ? "#fef2f2" : "#fafafa",
                      borderColor:
                        cheatingVerdict === "cheating" ? "#fca5a5" : "#e4e4e7",
                      color:
                        cheatingVerdict === "cheating" ? "#dc2626" : "#71717a",
                    }}
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                    >
                      <path d="M3 3L21 21M3 21L21 3" />
                    </svg>
                    Cheating
                  </button>
                  <button
                    onClick={() => setCheatingVerdict("not_cheating")}
                    className="flex-1 flex items-center justify-center gap-2 py-3 text-[11px] font-bold uppercase tracking-widest border transition-colors"
                    style={{
                      borderRadius: 0,
                      background:
                        cheatingVerdict === "not_cheating"
                          ? "#f0fdf4"
                          : "#fafafa",
                      borderColor:
                        cheatingVerdict === "not_cheating"
                          ? "#86efac"
                          : "#e4e4e7",
                      color:
                        cheatingVerdict === "not_cheating"
                          ? "#16a34a"
                          : "#71717a",
                    }}
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                    >
                      <path d="M2 8l4 4 8-8" />
                    </svg>
                    Not Cheating
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── RIGHT: Video Player (50%) ── */}
          <div
            className="flex flex-col bg-white overflow-y-auto hide-scroll"
            style={{ width: "50%" }}
          >
            {/* Header */}
            <div className="bg-white px-10 py-5 shrink-0">
              <div className="bg-white border border-zinc-200 px-8 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <p className="text-base font-bold text-zinc-900">
                    Interview Recording
                  </p>
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest"
                    style={{
                      background: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      color: "#15803d",
                      borderRadius: 0,
                    }}
                  >
                    Done
                  </span>
                </div>
                <span
                  className="text-[12px] text-zinc-400"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  {date
                    ? new Date(date).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : new Date().toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                </span>
              </div>
            </div>

            <VideoPlayer
              recordingUrl={recordingUrl}
              duration={duration}
              flaggedTimestamps={[
                ...(
                  reportData.transcriptAnalysis?.flagged_timestamps ?? []
                ).map((f) => ({ ...f, source: "transcript" })),
                ...(reportData.gazeAnalysis?.flagged_timestamps ?? []).map(
                  (f) => ({ ...f, source: "gaze" }),
                ),
              ]}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default ReportPage
